import get from 'lodash.get';
import {
    ZodArray,
    ZodCatch,
    type ZodError,
    ZodLazy,
    ZodLiteral,
    ZodObject,
    ZodOptional,
    type ZodType,
    ZodUnion,
    z,
} from 'zod';

import { logger } from '@libs/core';

export interface Options {
    path?: string[];
    breadcrumbs?: string;
}

const transforms = new WeakMap<ZodType, { schema: ZodType }>();
const fallbacks = new WeakSet<ZodType>();

function fmtPath(path: string[]) {
    return path.reduce((a, s) => (s.startsWith('[') ? a + s : a ? `${a}.${s}` : s), '');
}

function debug(error: ZodError, path: string[], breadcrumbs?: string) {
    error.issues.forEach(issue => {
        const location = fmtPath([...path, ...issue.path.map(p => (typeof p === 'number' ? `[${p}]` : String(p)))]);
        logger.debug(`✖ [${breadcrumbs}] ${issue.message} → ${location}`);
    });
}

function schemaMap(schemas: ZodType[]) {
    return schemas.reduce((map, schema) => {
        const resolved = schema instanceof ZodLazy ? schema.unwrap() : schema;
        const id = get(resolved, 'shape.sys.shape.contentType.shape.sys.shape.id') as unknown;
        const contentType = id instanceof ZodLiteral ? String(id.value) : undefined;

        if (contentType) map.set(contentType, resolved as ZodType);
        return map;
    }, new Map<string, ZodType>());
}

function array<S extends ZodType>(schema: S, options: Options) {
    const transform = z.array(z.unknown()).transform(items => {
        const byType = schema instanceof ZodUnion ? schemaMap(schema.options as ZodType[]) : null;

        return items.flatMap((item, i) => {
            const path = [...(options.path ?? []), `[${i}]`];
            const contentType = get(item, 'sys.contentType.sys.id') as unknown;
            const breadcrumbs = [options.breadcrumbs, contentType].filter(Boolean).join(' > ');

            const target = byType ? byType.get(contentType as string) : schema;
            if (!target) {
                logger.debug(`✖ [${breadcrumbs}] No schema → ${fmtPath(path)}`);
                return [];
            }

            const parsed = maybe(target, { path, breadcrumbs: options.breadcrumbs }).safeParse(item);
            if (parsed.success) return [parsed.data];

            debug(parsed.error, path, breadcrumbs);
            return [];
        });
    });

    transforms.set(transform, { schema });
    return transform as unknown as ZodType<z.infer<S>[]>;
}

export function maybe<T extends ZodType>(schema: T, options: Options = {}): T {
    const { breadcrumbs, path = [] } = options;

    if (schema instanceof ZodObject) {
        const sys = get(schema.shape, 'sys.shape.contentType.shape.sys.shape.id');
        const contentType = sys instanceof ZodLiteral ? String(sys.value) : undefined;
        const bc = [breadcrumbs, contentType].filter(Boolean).join(' > ');

        return schema.extend(
            Object.entries(schema.shape).reduce<Record<string, ZodType>>((acc, [key, value]) => {
                acc[key] = maybe(value, { path: [...path, key], breadcrumbs: bc });
                return acc;
            }, {})
        ) as never;
    }

    if (schema instanceof ZodArray) {
        return array(schema.unwrap() as ZodType, { path, breadcrumbs }) as never;
    }

    if (schema instanceof ZodUnion) {
        return z.union(schema.options.map(o => maybe(o as ZodType, { path, breadcrumbs }))) as never;
    }

    if (schema instanceof ZodLazy) {
        return z.lazy(() => maybe(schema.unwrap() as ZodType, { path, breadcrumbs })) as never;
    }

    if (schema instanceof ZodOptional) {
        const fallback = z
            .optional(maybe(schema.unwrap() as ZodType, { path, breadcrumbs }))
            .catch(({ error }: { error: ZodError }) => debug(error, path, breadcrumbs));

        fallbacks.add(fallback);
        return fallback as never;
    }

    if (schema instanceof ZodCatch) {
        const inner = schema.unwrap();

        if (inner instanceof ZodOptional && fallbacks.has(schema)) {
            return maybe(inner, { path, breadcrumbs }) as never;
        }
    }

    const element = transforms.get(schema)?.schema as ZodType;
    if (element) return array(element, { path, breadcrumbs }) as never;

    return schema;
}
